import { useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'suarte:art-games-radio:dock-y';
const DEFAULT_Y = 0.7;
const EDGE_CLEARANCE = 80;
const DRAG_THRESHOLD = 16;

function clampPosition(fraction) {
  const height = window.innerHeight;
  const min = Math.min(EDGE_CLEARANCE / height, 0.5);
  const max = Math.max(1 - EDGE_CLEARANCE / height, 0.5);
  return Math.min(max, Math.max(min, fraction));
}

function readPosition() {
  if (typeof window === 'undefined') return DEFAULT_Y;
  try {
    const saved = Number(window.sessionStorage.getItem(STORAGE_KEY));
    return clampPosition(Number.isFinite(saved) && saved > 0 ? saved : DEFAULT_Y);
  } catch (_error) {
    return clampPosition(DEFAULT_Y);
  }
}

export default function useRadioDockPosition() {
  const [fraction, setFraction] = useState(readPosition);
  const fractionRef = useRef(fraction);
  const dragRef = useRef(null);
  const suppressDragClickRef = useRef(false);

  useEffect(() => {
    const onResize = () => {
      setFraction(current => {
        const next = clampPosition(current);
        fractionRef.current = next;
        return next;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onPointerDown = event => {
    if (event.button !== 0 || dragRef.current) return;
    suppressDragClickRef.current = false;
    const bounds = event.currentTarget.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      startPointerX: event.clientX,
      startPointerY: event.clientY,
      startCenterY: bounds.top + bounds.height / 2,
      moved: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = event => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const distanceX = event.clientX - drag.startPointerX;
    const distanceY = event.clientY - drag.startPointerY;
    if (!drag.moved && Math.hypot(distanceX, distanceY) < DRAG_THRESHOLD) return;
    drag.moved = true;
    const next = clampPosition((drag.startCenterY + distanceY) / window.innerHeight);
    fractionRef.current = next;
    setFraction(next);
  };

  const endPointer = (event, cancelled = false) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (!cancelled && !drag.moved) onPointerMove(event);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (drag.moved) {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, String(fractionRef.current));
      } catch (_error) {
        // The control remains draggable when storage is unavailable.
      }
    }
    if (drag.moved || cancelled) suppressDragClickRef.current = true;
    dragRef.current = null;
  };

  const consumeDragClick = event => {
    if (event.detail === 0) return false;
    const suppress = suppressDragClickRef.current;
    suppressDragClickRef.current = false;
    return suppress;
  };

  return {
    fraction,
    onPointerDown,
    onPointerMove,
    onPointerUp: endPointer,
    onPointerCancel: event => endPointer(event, true),
    consumeDragClick,
  };
}
